'use client';

import React, { useState, cloneElement, isValidElement, useEffect } from 'react';
import GlobalFeedbackDrawer from '@/components/globalFeedbackDrawer';
import { GlobalFeedbackDrawerProps, SourceType } from '@/components/globalFeedbackDrawer/types';
import getShowEntrance from '@/utils/feedbackUtils';
import FeedbackTypeEnum from '@/app/feedback/components/feedbackType';

// Omit props that the wrapper will manage internally, then make most props optional
type BaseDrawerProps = Omit<GlobalFeedbackDrawerProps, 'openDrawer' | 'setOpenDrawer'>;

// All props from the drawer, except sourceType, are made optional for the trigger.
type OptionalDrawerProps = Partial<Omit<BaseDrawerProps, 'sourceType'>>;

export type FeedbackTriggerProps = OptionalDrawerProps & {
  sourceType: SourceType; // SourceType remains mandatory.
  forceShow?: boolean; // 强制显示，如果为 true 则直接显示，如果为 false 或未传值则根据 sourceType 获取配置
  children: React.ReactNode;
};

// SourceType 到 FeedbackTypeEnum 的映射
const getFeedbackTypeFromSourceType = (sourceType: SourceType): FeedbackTypeEnum => {
  const mapping: Record<SourceType, FeedbackTypeEnum> = {
    [SourceType.Feedback]: FeedbackTypeEnum.Feedback,
    [SourceType.Search]: FeedbackTypeEnum.Search,
    [SourceType.Recommend]: FeedbackTypeEnum.Recommend,
    [SourceType.Product]: FeedbackTypeEnum.Product,
    [SourceType.Recommend_Feedback]: FeedbackTypeEnum.Recommend_Feedback,
    [SourceType.Search_Feedback]: FeedbackTypeEnum.Search_Feedback,
    [SourceType.Category_Feedback]: FeedbackTypeEnum.Category_Feedback,
    [SourceType.Login_Feedback]: FeedbackTypeEnum.Login_Feedback,
    [SourceType.ProductGeneral]: FeedbackTypeEnum.ProductGeneral,
    [SourceType.ProductTranslate]: FeedbackTypeEnum.ProductTranslate,
    [SourceType.ProductReviewGeneral]: FeedbackTypeEnum.ProductReviewGeneral,
    [SourceType.ProductReviewTranslate]: FeedbackTypeEnum.ProductReviewTranslate,
    [SourceType.Marketing_Feedback]: FeedbackTypeEnum.Marketing_Feedback,
    [SourceType.ScreenShot]: FeedbackTypeEnum.ScreenShot,
  };
  return mapping[sourceType];
};

const FeedbackTrigger: React.FC<FeedbackTriggerProps> = ({
  children,
  sourceType,
  forceShow,
  ...drawerProps
}) => {
  const [openDrawer, setOpenDrawer] = useState(false);
  const [shouldShowFeedback, setShouldShowFeedback] = useState(forceShow);

  useEffect(() => {
    // 如果 forceShow 为 true，直接显示
    if (forceShow !== undefined) {
      return;
    }

    // 如果 forceShow 为 false 或未传值，根据 sourceType 获取配置
    const checkShowEntrance = async () => {
      try {
        const feedbackType = getFeedbackTypeFromSourceType(sourceType);
        const shouldShow = await getShowEntrance(feedbackType);
        setShouldShowFeedback(shouldShow);
      } catch (error) {
        console.error('检查反馈入口显示状态失败:', error);
        // 发生错误时默认不显示
        setShouldShowFeedback(false);
      }
    };

    checkShowEntrance();
  }, [sourceType, forceShow]);

  const handleOpen = () => {
    setOpenDrawer(true);
  };

  // 如果不应该显示反馈入口，直接返回子组件但不添加触发功能
  if (!shouldShowFeedback) {
    return <>{children}</>;
  }

  // Ensure children is a single valid React element to attach the onClick handler
  if (!isValidElement(children) || React.Children.count(children) > 1) {
    console.error('FeedbackTrigger expects a single React element as its child.');
    // Render children as is without the trigger functionality in case of invalid children
    return <>{children}</>;
  }

  // Add the onClick handler to the trigger element
  const trigger = cloneElement(children as React.ReactElement, {
    onClick: (...args: any[]) => {
      // Call original onClick if it exists
      if (children.props.onClick) {
        children.props.onClick(...args);
      }
      handleOpen();
    },
  });

  return (
    <>
      {trigger}
      <GlobalFeedbackDrawer
        {...drawerProps}
        sourceType={sourceType}
        openDrawer={openDrawer}
        setOpenDrawer={setOpenDrawer}
      />
    </>
  );
};

export default FeedbackTrigger;
